import { useAuth } from '@/contexts/auth-context';
import { SUBSCRIPTION_QUERY_KEY } from '@/hooks/queries/use-subscription-query';
import { usePusher } from '@/hooks/usePusher';
import api from '@/libs/axiosInstance';
import type { ApiResponse, SubscriptionPriceDropStatus } from '@fluenceur/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

export const SUBSCRIPTION_PRICE_DROP_QUERY_KEY = ['subscription-price-drop'] as const;

const fetchSubscriptionPriceDrop = async (): Promise<SubscriptionPriceDropStatus> => {
  const response = await api.get<ApiResponse<SubscriptionPriceDropStatus>>(
    '/dashboard/subscription/price-drop'
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to fetch the subscription price drop');
  }

  return response.data.data;
};

interface UseSubscriptionPriceDropOptions {
  enabled?: boolean;
}

/**
 * Read whether the organization's live subscription is billed above the
 * current catalogue price, and so has a lower price waiting for approval.
 *
 * Once the merchant approves the replacement on Shopify, the backend stops
 * offering it. That approval reaches us through the `app_subscriptions/update`
 * webhook, which fires the `subscription-activated` Pusher event: listening to
 * it lets the card go away without a reload, even when the post-return refetch
 * landed before the webhook did.
 */
export function useSubscriptionPriceDropQuery(options: UseSubscriptionPriceDropOptions = {}) {
  const { enabled = true } = options;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: SUBSCRIPTION_PRICE_DROP_QUERY_KEY,
    queryFn: fetchSubscriptionPriceDrop,
    staleTime: 1000 * 60,
    retry: 1,
    enabled,
  });

  const handleSubscriptionActivated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_PRICE_DROP_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
  }, [queryClient]);

  const brandId = user?.currentBrandId;

  usePusher({
    channelName: brandId ? `private-brand-${brandId}` : '',
    eventName: 'subscription-activated',
    callback: handleSubscriptionActivated,
    enabled: enabled && !!brandId,
  });

  return query;
}
